import { JEFE_2 } from '../../../data/boss-objects';
import { PLAYER_SCREEN_X, SIM_HZ, VIEW_WIDTH } from '../../../data/tuning';
import { spawnObject, ticksOf, type PatternEnv } from '../attacks';
import type { BossFight } from '../boss-fight';
import { registerPattern } from '../patterns';
import type { TickContext } from '../../tick';

/**
 * Attack patterns of JEFE-2 El Jinete (GDD §9.2, issue #110). Each is called once per tick while its phase is active
 * and the boss is not in transition, so nothing is launched during one. The counters restart when the phase changes.
 */

function syncPhase(fight: BossFight, env: PatternEnv): void {
  if (env.attacks.phase === fight.phase) return;
  env.attacks.phase = fight.phase;
  env.attacks.cadence = 0;
  env.attacks.pending = [];
}

/** Ticks a horseshoe takes to cross from the boss (80 % of the view) to the pilot (30 %) at its speed. */
const TICKS_TO_PILOT = Math.round(((0.8 - PLAYER_SCREEN_X) * VIEW_WIDTH * SIM_HZ) / JEFE_2.horseshoeSpeed);
/** And to its turning point, from the boss to the fraction of the view where it turns. */
const TICKS_TO_TURN = Math.round(((0.8 - JEFE_2.turnFromView) * VIEW_WIDTH * SIM_HZ) / JEFE_2.horseshoeSpeed);

/**
 * A horseshoe thrown by the boss towards the height of the pilot at this moment. `boomerang` makes it turn inside the
 * view and come back through the pilot's column; without it the horseshoe flies straight on.
 */
export function throwHorseshoe(fight: BossFight, context: TickContext, env: PatternEnv, boomerang: boolean) {
  const { attacks, run } = env;
  const target = run.core.player.y;
  const ticks = boomerang ? TICKS_TO_TURN : TICKS_TO_PILOT;
  return spawnObject(attacks, context, 'horseshoe', {
    x: fight.x,
    y: fight.y,
    vx: (run.scrollSpeed - JEFE_2.horseshoeSpeed) / SIM_HZ,
    vy: (target - fight.y) / ticks,
    ...(boomerang && { boomerang: { turnFromView: JEFE_2.turnFromView, speed: JEFE_2.horseshoeSpeed, returning: false } }),
  });
}

/** Phase 1, "Herraduras": a boomerang horseshoe every 2 s. */
registerPattern('herraduras', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  env.attacks.cadence += 1;
  if (env.attacks.cadence < ticksOf(JEFE_2.horseshoeEverySeconds)) return;
  env.attacks.cadence = 0;
  throwHorseshoe(fight, context, env, true);
});

/** Phase 2, "Salsa": exactly one straight horseshoe on every hit of the son clave 3-2 (RHYTHM_CLAVE_HIT, #109). */
registerPattern('salsa', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  if (!env.run.rhythm?.current?.claveHit) return;
  throwHorseshoe(fight, context, env, false);
});

/** Phase 3, "Los Caballeros": a portrait wall every 8.5 u with a gap that swings and is at its base when passed. */
registerPattern('los-caballeros', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  const { attacks, run, view } = env;
  const every = Math.round((JEFE_2.wallSpacing / run.scrollSpeed) * SIM_HZ);
  attacks.cadence += 1;
  if (attacks.walls > 0 && attacks.cadence < every) return;
  attacks.cadence = 0;
  // The base of the gap moves at most wallMaxStep from the previous wall, inside the band that keeps the swing in 3..10.
  const previous = attacks.lastBase ?? (JEFE_2.wallBaseLow + JEFE_2.wallBaseHigh) / 2;
  const wanted = JEFE_2.wallBaseLow + attacks.rng() * (JEFE_2.wallBaseHigh - JEFE_2.wallBaseLow);
  const base = Math.min(JEFE_2.wallBaseHigh, Math.max(JEFE_2.wallBaseLow, Math.min(previous + JEFE_2.wallMaxStep, Math.max(previous - JEFE_2.wallMaxStep, wanted))));
  attacks.lastBase = base;
  const x = view.right + JEFE_2.wallWidth;
  // The wall is world-fixed: the plane reaches it when the scroll has covered the distance.
  const passTick = run.core.tick + Math.round(((x - run.core.player.x) / run.scrollSpeed) * SIM_HZ);
  const portrait = JEFE_2.portraits[attacks.walls % JEFE_2.portraits.length]!;
  attacks.walls += 1;
  spawnObject(attacks, context, 'portrait-wall', { x, y: 6, wall: { portrait, base, passTick } });
});
